import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Calendar, Percent, Edit, Trash2, Eye, EyeOff } from "lucide-react";
import { Promotion, useUpdatePromotion, useDeletePromotion } from "@/hooks/usePromotions";
import { format } from "date-fns";
import EditPromotionDialog from "./EditPromotionDialog";

interface PromotionCardProps {
  promotion: Promotion;
  services: Array<{ id: string; name: string }>;
  onPromotionUpdated: () => void;
}

const PromotionCard = ({ promotion, services, onPromotionUpdated }: PromotionCardProps) => {
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const updatePromotion = useUpdatePromotion();
  const deletePromotion = useDeletePromotion();

  const handleToggleActive = async () => {
    try {
      await updatePromotion.mutateAsync({
        id: promotion.id,
        is_active: !promotion.is_active,
      });
      onPromotionUpdated();
    } catch (error) {
      console.error('Error toggling promotion:', error);
    }
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this promotion?')) {
      try {
        await deletePromotion.mutateAsync(promotion.id);
        onPromotionUpdated();
      } catch (error) {
        console.error('Error deleting promotion:', error);
      }
    }
  };

  const isActive = promotion.is_active && 
    new Date(promotion.start_date) <= new Date() && 
    new Date(promotion.end_date) >= new Date();

  const isExpired = new Date(promotion.end_date) < new Date();
  const isUpcoming = new Date(promotion.start_date) > new Date();

  const getStatusBadge = () => {
    if (!promotion.is_active) {
      return <Badge variant="secondary">Inactive</Badge>;
    }
    if (isExpired) {
      return <Badge variant="destructive">Expired</Badge>;
    }
    if (isUpcoming) {
      return <Badge variant="outline">Upcoming</Badge>;
    }
    return <Badge variant="default">Active</Badge>;
  };

  const getDaysDisplay = () => {
    if (promotion.days_of_week.length === 7) {
      return "Every day";
    }
    
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const selectedDayNames = promotion.days_of_week
      .sort()
      .map(day => dayNames[day]);
    
    if (selectedDayNames.length <= 3) {
      return selectedDayNames.join(', ');
    }
    
    return `${selectedDayNames.slice(0, 2).join(', ')} +${selectedDayNames.length - 2} more`;
  };

  return (
    <>
      <Card className={`${isActive ? 'border-green-200 bg-green-50' : ''}`}>
        <CardHeader>
          <div className="flex items-start justify-between">
            <div>
              <CardTitle className="text-lg">{promotion.name}</CardTitle>
              <p className="text-sm text-muted-foreground mt-1">
                {promotion.services?.name}
              </p>
            </div>
            {getStatusBadge()}
          </div>
        </CardHeader>
        
        <CardContent className="space-y-4">
          <div className="flex items-center space-x-4 text-sm">
            <div className="flex items-center space-x-1">
              <Percent className="h-4 w-4" />
              <span className="font-semibold">
                {promotion.discount_type === 'percentage' 
                  ? `${promotion.discount_percentage}% OFF`
                  : `£${promotion.discount_amount} OFF`
                }
              </span>
            </div>
            <div className="flex items-center space-x-1">
              <Calendar className="h-4 w-4" />
              <span>
                {format(new Date(promotion.start_date), 'MMM d')} - {format(new Date(promotion.end_date), 'MMM d, yyyy')}
              </span>
            </div>
          </div>

          {promotion.description && (
            <p className="text-sm text-muted-foreground">{promotion.description}</p>
          )}

          <div className="text-xs text-gray-600 mt-2">
            <span className="font-medium">Days: </span>
            {getDaysDisplay()}
          </div>

          <div className="flex justify-between items-center pt-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleToggleActive}
              disabled={updatePromotion.isPending}
            >
              {promotion.is_active ? (
                <>
                  <EyeOff className="h-4 w-4 mr-1" />
                  Deactivate
                </>
              ) : (
                <>
                  <Eye className="h-4 w-4 mr-1" />
                  Activate
                </>
              )}
            </Button>

            <div className="flex space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setEditDialogOpen(true)}
              >
                <Edit className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleDelete}
                disabled={deletePromotion.isPending}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <EditPromotionDialog
        promotion={promotion}
        services={services}
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        onPromotionUpdated={onPromotionUpdated}
      />
    </>
  );
};

export default PromotionCard;